/**
 * Zod schemas for fleet-api auth endpoint payloads.
 *
 * Provides runtime validation of API responses and inferred TypeScript types.
 * These types are the shared contract between the auth API layer, AuthProvider,
 * and any consumer of useAuth().
 */
import { z } from 'zod';

/** Nested org summary embedded in user/token responses. */
export const OrgSummarySchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
});

/**
 * User shape as returned by GET /v1/auth/me and embedded in POST /v1/auth/login.
 *
 * actor_type distinguishes client portal users from internal staff.
 * role drives the fine-grained permission set within the actor_type.
 *
 * Phase 3.1 additions (per ADR-006 + Phase 1.5 Plan 04 wire shape):
 * - impersonating_as: true on impersonation tokens minted via
 *   POST /v1/admin/impersonate/<org_id>; absent or false on regular tokens.
 *   Defaults to false so legacy callers and pre-Phase-3.1 fixtures continue
 *   to behave as if the staff user is not impersonating.
 * - target_org: populated only when impersonating_as is true; carries the
 *   client org the staff member is currently viewing as.
 * - impersonation_session_id: opaque server-side session identifier; used by
 *   the audit trail. Read by the banner (Phase 3.1 Plan 03) for display only.
 */
export const UserSchema = z.object({
  user_id: z.string(),
  email: z.string().email(),
  // fleet-api collapsed the actor enum to two values: 'internal_staff' for
  // staff (sigsentry et al) and a single 'client_user' bucket for both
  // client_viewer and client_admin roles. Role is the finer-grained gate.
  actor_type: z.enum(['client_user', 'internal_staff']),
  role: z.enum(['staff_admin', 'staff_operator', 'client_viewer', 'client_admin']),
  // fleet-api /v1/auth/me returns `org` as an OrgSummary or null. There is
  // no separate `org_id` field on the wire; derive id from `user.org?.id`
  // if you need it. `org_id` is kept here as an optional read-through
  // alias for older test fixtures and is never required.
  org: OrgSummarySchema.nullable().optional(),
  org_id: z.string().nullable().optional(),
  // Phase 3.1: impersonation claims (see file docstring above).
  impersonating_as: z.boolean().optional().default(false),
  target_org: OrgSummarySchema.nullable().optional(),
  impersonation_session_id: z.string().nullable().optional(),
});

/** Inferred User TypeScript type. */
export type User = z.infer<typeof UserSchema>;

/** Response shape from POST /v1/auth/login.
 *
 * fleet-api returns just the token pair; the user object is fetched in a
 * follow-up GET /v1/auth/me call inside AuthProvider.login(). `token_type`
 * is lowercase ("bearer") on the wire (RFC 6749 says the value is
 * case-insensitive but fleet-api emits lowercase). `user` is kept as an
 * optional pass-through for older fixtures + ergonomic mocking; production
 * always reads it via meRequest().
 */
export const LoginResponseSchema = z.object({
  access_token: z.string(),
  refresh_token: z.string(),
  token_type: z.string(),
  expires_in: z.number().optional(),
  user: UserSchema.optional(),
});

/** Inferred LoginResponse type. */
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

/** Response shape from POST /v1/auth/refresh (rotation-on-use).
 *
 * fleet-api emits `token_type: "bearer"` lowercase. Accept any string per
 * the same pragmatism as LoginResponseSchema.
 */
export const RefreshResponseSchema = z.object({
  access_token: z.string(),
  refresh_token: z.string(),
  token_type: z.string(),
  expires_in: z.number().optional(),
});

/** Inferred RefreshResponse type. */
export type RefreshResponse = z.infer<typeof RefreshResponseSchema>;

/**
 * Typed login error thrown by useAuth().login.
 *
 * Distinguishes credential failures from rate-limiting so the UI
 * can show the appropriate copy without checking HTTP status codes directly.
 */
export class LoginError extends Error {
  /** 'invalid_credentials' | 'rate_limited' */
  public readonly code: string;
  /** Seconds until the rate-limit window resets (only present for rate_limited). */
  public readonly retry_after?: number;

  constructor(code: string, retry_after?: number) {
    super(code);
    this.name = 'LoginError';
    this.code = code;
    this.retry_after = retry_after;
  }
}

/**
 * AuthContext value shape exposed by useAuth().
 *
 * status drives loading vs authenticated vs unauthenticated branches.
 * login/logout are async actions; login returns the resolved User on success.
 *
 * Phase 3.1 Plan 03 additions (per ADR-006 section 3):
 *   startImpersonation(orgId): swap active staff tokens for an impersonation
 *     pair targeting orgId; updates context user state; ImpersonationRedirect
 *     handles the rising-edge navigation to /now.
 *   exitImpersonation(): close the active impersonation session, restore
 *     staff tokens from sidecar, refetch /me; ImpersonationRedirect handles
 *     the falling-edge navigation to /admin/fleet.
 */
export interface AuthContextValue {
  user: User | null;
  status: 'initializing' | 'authenticated' | 'unauthenticated';
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  startImpersonation: (orgId: string) => Promise<User>;
  exitImpersonation: () => Promise<User>;
}
